import { defineStore, storeToRefs } from 'pinia';
import { uniqueId } from 'lodash-es';
import type { Cartv2PackageDisplayModel, Cartv2PackageItemDisplayModel } from '@shared/models/carts/cartPackages';
import type { PackageDisplayModel } from '@shared/models/packages/packages';
import type { SubjectImageDisplayModel } from '@shared/models/subjects/images';
import type { SubjectFieldDisplayModel } from '@shared/models/subjects/subjectFields';
import type { SubjectDisplayModel } from '@shared/models/subjects/subjects';
import { getAutoFillImageNodes } from '@shared/services/packageNodes.service';
import { allowCrop } from '@shared/services/crop';
import type { identifier } from '@core/models/base';
import type { ServiceResult } from '@core/models/actionResponse';
import { usePackageAddonService } from '@apps/job-editor/services/packageAddon.service';
import type { GreenScreenBackgroundDisplayModel } from '@shared/models/packages/greenScreenBackgrounds';
import type { BackgroundDisplayModel } from '@shared/models/packages/backgroundSets';
import { useCartStore } from '@apps/customer/stores/cart.store';
import { useCustomerStore } from '@apps/customer/stores/customer.store';
import {
  SINGLE_DIGITAL_DOWNLOAD,
  SINGLE_DIGITAL_DOWNLOAD_RSCODE,
  WHOLE_DIGITAL_DOWNLOAD,
  WHOLE_DIGITAL_DOWNLOAD_RSCODE,
  isLegacyWholeDigitalDownload,
} from '@shared/models/products/productLookup';
import { useCurrencyArithmetic } from '@shared/composables/useCurrencyArithmetic';
import { fetchCart } from '@apps/cart/services/cart.service';
import type { MCropperResult } from '@shared/components/MCropper/models';
import {
  applyGroupImageToItemNodes,
  applyIndividualImageToItemNodes,
  getCartPackageFromPackageAndImage,
  getCartPackageFromServer,
} from '../services/cartPackageModels.service';
import { applySoftCropDefaultsToItem } from '../services/softCropDefaults.service';
import { resolveBrowserImageDimensions } from '@shared/services/crop';
import { applyGlobalCropResultToItem } from '../services/globalCrop.service';
import {
  areImageNodesComplete,
  consumerTextNodeFillOptions,
  isItemComplete,
  areTextNodesComplete,
  isMultiImageSelectionComplete,
} from '../services/cartPackageNode.service';
import { createCartPackage, patchCartPackageDetailsToServer } from '../services/cartPackage.service';
import type { ICartPackageStoreState, ICartPackageStoreItem } from './cartPackage.cache';

const { validatePackage, hasStandardPackage, isAddon } = usePackageAddonService();
const { multiply, subtract, add } = useCurrencyArithmetic();
export const useCartPackageStore = defineStore('cartPackage', {
  state: (): ICartPackageStoreState => ({
    items: [],
    code: undefined,
    saving: false,
    anchorImage: undefined,
    orderedAnchorImageIds: [],
  }),

  getters: {
    total: (state): number => {
      const cartStore = useCartStore();
      const { total } = storeToRefs(cartStore);
      const itemPrices = state.items?.map((i) => multiply(i.jobPackage.retailCost, i.cartPackage.quantity)) ?? [0];
      return add(total.value, ...itemPrices);
    },
    hasStandardPackage: (state) => hasStandardPackage(state.items?.map((i) => i.jobPackage)),

    addonPackages: (state) => (state.items.length ? state.items.filter((i) => isAddon(i.jobPackage)) : []),
    getItemByPackage(state) {
      const fn = (pkg: PackageDisplayModel) => state.items.find((item) => item.jobPackage.id === pkg.id);
      return fn;
    },

    editableItems(state): Map<Cartv2PackageDisplayModel, Cartv2PackageItemDisplayModel[]> {
      const customerStore = useCustomerStore();
      return new Map<Cartv2PackageDisplayModel, Cartv2PackageItemDisplayModel[]>(
        state.items.map((item) => [
          item.cartPackage,
          item.cartPackage?.cartPackageItems.filter(
            (i) =>
              (!customerStore.isPrePayRspo && i.imageNodes.length) ||
              i.textNodes.filter((n) => {
                if (customerStore.hasImportedData) return !n.autoFilled;
                return n;
              }).length ||
              i.productCode === WHOLE_DIGITAL_DOWNLOAD ||
              i.productCode === SINGLE_DIGITAL_DOWNLOAD ||
              i.productCode === SINGLE_DIGITAL_DOWNLOAD_RSCODE ||
              i.productCode === WHOLE_DIGITAL_DOWNLOAD_RSCODE,
          ) ?? [],
        ]),
      );
    },

    /**
     Check if the cart package store has any editable items or the job has Green Screens or Backgrounds.
     Editable items are items that have image nodes (when hosted) or text nodes.
     Editable items are digitale items
     Editable items are available to customers with green screen backgrounds or backgrounds.

     Also true while the job's green screen backgrounds are still UNKNOWN. This getter decides
     whether the package-options step can be skipped entirely, so "we haven't loaded the
     backgrounds yet" has to mean "don't skip it" — otherwise a package with no editable items
     goes straight into the cart with no background on a job that requires one.
    */
    hasEditableItems(state) {
      const customerStore = useCustomerStore();
      const editableItems = this.editableItems as Map<Cartv2PackageDisplayModel, Cartv2PackageItemDisplayModel[]>;
      const values = Array.from(editableItems?.values());
      return (
        (!!values.length && values.some((v) => v.length)) ||
        !customerStore.greenScreensKnown ||
        customerStore.hasGreenScreens ||
        customerStore.hasBackgrounds
      );
    },

    getDefaultImageNode(state) {
      const fn = (pkg: PackageDisplayModel) => {
        const item = this.getItemByPackage(pkg);
        return item?.jobPackage ? getAutoFillImageNodes(item.jobPackage)[0] : null;
      };

      return fn;
    },

    packageHasCroppableItems(state) {
      const fn = (pkg: PackageDisplayModel) => {
        const item = this.getItemByPackage(pkg);
        if (item?.jobPackage) {
          const isV3 = useCustomerStore().job?.isV3 ?? false;
          return item.jobPackage.items.some((i) => allowCrop(item.jobPackage as PackageDisplayModel, i, isV3));
        }

        return false;
      };

      return fn;
    },

    hasCroppableItems: (state) => {
      const isV3 = useCustomerStore().job?.isV3 ?? false;
      for (const item of state.items) {
        if (item.jobPackage.items.some((i) => allowCrop(item.jobPackage, i, isV3))) {
          return true;
        }
      }

      return false;
    },

    packageHasAutoFillableNodes(state) {
      const fn = (pkg: PackageDisplayModel) => {
        const item = this.getItemByPackage(pkg);
        return item?.jobPackage && getAutoFillImageNodes(item.jobPackage).length;
      };

      return fn;
    },

    hasAutoFillableNodes: (state) =>
      state.items.filter((i) => i.jobPackage).some((i) => getAutoFillImageNodes(i.jobPackage!).length),

    allPackageItemsComplete(state) {
      const isV3 = !!useCustomerStore().job?.isV3;
      const fn = (pkg: PackageDisplayModel) => {
        const item = this.getItemByPackage(pkg);
        return item?.cartPackage?.cartPackageItems.every((i) => isItemComplete(i, isV3));
      };

      return fn;
    },

    allItemsComplete: (state) => {
      const isV3 = !!useCustomerStore().job?.isV3;
      return state.items.flatMap((i) => i.cartPackage.cartPackageItems).every((i) => isItemComplete(i, isV3));
    },

    allTextItemsComplete: (state) => {
      const isV3 = !!useCustomerStore().job?.isV3;
      return state.items.flatMap((i) => i.cartPackage.cartPackageItems).every((i) => areTextNodesComplete(i, isV3));
    },

    allImageItemsComplete: (state) => {
      // Legacy "M2" is a whole-subject download (no image collection in cart — all subject images
      // are added at submit), so it has no image nodes to complete and must not block checkout.
      const isV3 = !!useCustomerStore().job?.isV3;
      return state.items
        .flatMap((i) => i.cartPackage.cartPackageItems)
        .filter((item) => !isLegacyWholeDigitalDownload(item.productCode, isV3))
        .every(areImageNodesComplete);
    },

    /** All multi-image digital download items have images selected */
    allMultiImageSelectionsComplete: (state) => {
      const customerStore = useCustomerStore();
      if (customerStore.isPrePay) return true;

      // Multi-image selection is a V3/RSPO-only feature. The V3 multi-image product codes overlap
      // with legacy RS codes (e.g. "Dk"), so without this gate legacy hosted galleries would prompt
      // customers to "select images for your multi-image digital download(s)" with no selector shown.
      // Legacy jobs auto-include all of the subject's images at order-XML generation, so nothing to do.
      if (!customerStore.job?.isV3) return true;

      return state.items.flatMap((i) => i.cartPackage.cartPackageItems).every(isMultiImageSelectionComplete);
    },

    packageImageNodes(state) {
      const fn = (pkg: PackageDisplayModel) => {
        const item = this.getItemByPackage(pkg);
        return item?.jobPackage?.items.map((i) => i.imageNodes).flat() || [];
      };

      return fn;
    },

    allImageNodes: (state) => state.items.flatMap((i) => i.jobPackage.items).flatMap((i) => i.imageNodes),

    packageGreenScreenSelected(state) {
      const fn = (pkg: PackageDisplayModel) => {
        const item = this.getItemByPackage(pkg);
        return !!item?.cartPackage?.greenScreen?.id;
      };

      return fn;
    },

    greenScreensSelected: (state) => state.items.every((i) => i.cartPackage?.greenScreen?.id),

    /**
     * Check if all packages that require backgrounds have a background selected.
     * For prepay orders: all packages require a background (images aren't assigned yet)
     * For other orders: only packages with PNG images require a background
     */
    backgroundsSelectedForPngPackages(state) {
      const customerStore = useCustomerStore();

      const isPngString = (str: string): boolean => {
        if (!str) return false;
        const strWithoutQuery = str.split('?')[0];
        return strWithoutQuery.toLowerCase().endsWith('.png');
      };

      const packageHasPngImage = (cartPackage: Cartv2PackageDisplayModel): boolean => {
        for (const item of cartPackage.cartPackageItems || []) {
          for (const imageNode of item.imageNodes || []) {
            // Check multiple sources for PNG detection
            if (
              isPngString(imageNode.fileName || '') ||
              isPngString(imageNode.imageUrl || '') ||
              isPngString(imageNode.imageThumbnailUrl || '')
            ) {
              return true;
            }
          }
        }
        return false;
      };

      // Determine if a package requires a background selection
      const packageRequiresBackground = (cartPackage: Cartv2PackageDisplayModel): boolean => {
        if (customerStore.isPrePay) {
          // For prepay, all packages require background selection (images aren't assigned yet)
          return true;
        }
        // For hosted/gallery, only PNG packages require backgrounds
        return packageHasPngImage(cartPackage);
      };

      // For each package that requires a background, check if one is selected
      return state.items.every((i) => {
        if (!packageRequiresBackground(i.cartPackage)) {
          return true; // Background not required for this package
        }
        return !!i.cartPackage?.background?.id;
      });
    },
  },
  actions: {
    initializeFromImageAndPackage(
      pkg: PackageDisplayModel,
      subject: SubjectDisplayModel,
      image?: SubjectImageDisplayModel,
    ) {
      if (!this.items) {
        this.items = [];
      }

      const item: ICartPackageStoreItem = {
        id: uniqueId(),
        subject: subject,
        jobPackage: pkg,
        // V3 (RSPO) consumer ordering: never pre-fill email fields from the subject's data (imported
        // or entered during build) — the customer is prompted for the address their files should go
        // to. Legacy has no field to prompt with, so it keeps auto-filling. See
        // consumerTextNodeFillOptions.
        cartPackage: getCartPackageFromPackageAndImage(
          pkg,
          subject,
          image,
          consumerTextNodeFillOptions(!!useCustomerStore().job?.isV3),
        ),
      };

      this.items.push(item);
    },

    initializeFromServer(
      cartPackage: Cartv2PackageDisplayModel,
      jobPackage: PackageDisplayModel,
      subject: SubjectDisplayModel,
      hasImportedData: boolean,
    ) {
      if (!this.items) {
        this.items = [];
      }

      const processedCartPackage = getCartPackageFromServer(
        cartPackage,
        subject,
        hasImportedData,
        jobPackage,
        consumerTextNodeFillOptions(!!useCustomerStore().job?.isV3),
      );

      // Hydrate background from job's background set if backgroundId is present but background object is not
      // The API may return backgroundId as a number but not the full background object
      const rawCartPackage = cartPackage as Cartv2PackageDisplayModel & { backgroundId?: number };

      if (rawCartPackage.backgroundId && !processedCartPackage.background) {
        const customerStore = useCustomerStore();
        const background = customerStore.backgrounds.find((bg) => bg.id === rawCartPackage.backgroundId);
        if (background) {
          processedCartPackage.background = background;
        }
      }

      const item: ICartPackageStoreItem = {
        id: uniqueId(),
        subject: subject,
        jobPackage: jobPackage,
        cartPackage: processedCartPackage,
      };

      this.items.push(item);
    },

    checkPriceAddonStatus() {
      if (this.items?.length) {
        this.items = this.items.filter((i) =>
          validatePackage(i.cartPackage, multiply(i.jobPackage.retailCost, i.cartPackage.quantity), this.total),
        );
      }
    },

    updateCartPackageQuantity(pkg: Cartv2PackageDisplayModel, quantity: number) {
      if (pkg) {
        const item = this.items?.find((i) => i.cartPackage.id === pkg.id);
        if (item && quantity === 0) {
          this.removeItem(item);
        } else if (item) {
          item.cartPackage.quantity = quantity;
          this.checkPriceAddonStatus();
        }
      }
    },

    removeItem(item?: ICartPackageStoreItem) {
      if (item) {
        this.items = this.items?.filter((i) => i !== item);

        if (!this.hasStandardPackage) {
          this.clear();
        } else this.checkPriceAddonStatus();
      }
    },

    removeItemByJobPackage(pkg: PackageDisplayModel) {
      this.removeItem(this.getItemByPackage(pkg));
    },

    removeItemByItemId(storeItemId: string) {
      this.removeItem(this.items.find((i) => i.id === storeItemId));
    },

    removeItemByCartPackage(pkg: Cartv2PackageDisplayModel) {
      this.removeItem(this.items?.find((i) => i.cartPackage.id === pkg.id));
    },

    useCode(code: string) {
      this.code = code;
    },

    setGreenScreen(storeItem: ICartPackageStoreItem, greenScreen: GreenScreenBackgroundDisplayModel) {
      storeItem.cartPackage.greenScreen = greenScreen;
    },

    setBackground(storeItem: ICartPackageStoreItem, background: BackgroundDisplayModel | null) {
      storeItem.cartPackage.background = background ?? undefined;
    },

    updateCartPackage(storeItemId: string, update: Cartv2PackageDisplayModel) {
      const item = this.items.find((i) => i.id === storeItemId);
      if (item) {
        item.cartPackage = update;
      }
    },

    updateItems(items: ICartPackageStoreItem[]) {
      this.items = items;
    },

    /**
     * Step 3 (RSPO hosted): apply ONE chosen individual image and/or ONE chosen group
     * image across EVERY selected package's items. Individual fills individual nodes,
     * group fills group nodes; each leaves the other node type untouched, so a mixed
     * package can receive both. Pass only the image(s) the packages actually require.
     */
    assignImagesToAllPackages(images: {
      individualImage?: SubjectImageDisplayModel;
      groupImage?: SubjectImageDisplayModel;
    }) {
      const assigned = [images.individualImage, images.groupImage].filter(
        (image): image is SubjectImageDisplayModel => !!image,
      );

      this.items?.forEach((storeItem) => {
        storeItem.cartPackage.cartPackageItems = storeItem.cartPackage.cartPackageItems.map((item) => {
          let next = item;
          if (images.individualImage) next = applyIndividualImageToItemNodes(images.individualImage, next);
          if (images.groupImage) next = applyGroupImageToItemNodes(images.groupImage, next);
          return next;
        });
      });

      this.mergeClaimedImages(assigned);
    },

    /**
     * Keep every package's `subject.images` in step with photos claimed AFTER that snapshot was taken.
     * It is loaded when SelectPackagePage loads, and in a CONSUMER-DERIVED gallery a photo becomes a
     * SubjectImage only when the consumer claims it — during image selection, or later from a step-4
     * change-photo pick. Everything downstream resolves node.subjectImageId through this collection
     * (the global crop step's image list, applySoftCropDefaults, each preview card's subjectImages),
     * so an unmerged image is silently skipped: the classic symptom is being asked to crop only the
     * individual when the package needs individual + group.
     */
    mergeClaimedImages(images: SubjectImageDisplayModel[]) {
      const claimed = images.filter((image) => image.id != null);
      if (!claimed.length) return;

      this.items?.forEach((storeItem) => {
        if (!storeItem.subject) return;
        const existing = storeItem.subject.images ?? [];
        const known = new Set(existing.map((image) => image.id));
        const additions = claimed.filter((image) => !known.has(image.id));
        if (additions.length) storeItem.subject = { ...storeItem.subject, images: [...existing, ...additions] };
      });

      // Fire-and-forget: the selection caller navigates immediately after this, so the stamping can't
      // be awaited here. It usually lands well before the step-4 cards mount (they await
      // applySoftCropDefaults first, which resolves the same memoized dimensions); MLivePreview's
      // own await-before-deciding gate covers any node the stamping hasn't reached yet.
      for (const image of claimed) void this.stampImageDimensions(image);
    },

    /**
     * Stamp `imageWidth`/`imageHeight` (post-EXIF browser dimensions) on every node fed by this
     * subject image, across every package. MLivePreview's -vert/-horiz template decision reads
     * these; without them a swappable item can render the wrong orientation until the thumbnail
     * happens to finish loading inside that one preview instance.
     */
    async stampImageDimensions(image: SubjectImageDisplayModel) {
      if (!image.imageThumbnailUrl) return;
      const dims = await resolveBrowserImageDimensions(image.imageThumbnailUrl);
      if (!dims) return;
      for (const storeItem of this.items) {
        for (const item of storeItem.cartPackage?.cartPackageItems ?? []) {
          for (const node of item.imageNodes ?? []) {
            if (node.subjectImageId === image.id) {
              node.imageWidth = dims.width;
              node.imageHeight = dims.height;
            }
          }
        }
      }
    },

    /**
     * Step 4 (RSPO hosted): apply the studio's soft crop as each node's DEFAULT framing across all
     * items, so previews centre on the subject instead of dead-centre — one framing intent per image
     * trickles to every node at its own slot. Skips nodes that already carry `cropDimensions` (a
     * per-item override the consumer set), so re-running is safe and never clobbers manual edits.
     * Async + heavy (mounts a hidden cropper per node); call once when the item view loads.
     */
    async applySoftCropDefaults() {
      for (const storeItem of this.items) {
        const imagesById = new Map((storeItem.subject?.images ?? []).map((img) => [img.id, img]));
        if (!imagesById.size) continue;
        const cartPackageItems = await Promise.all(
          (storeItem.cartPackage?.cartPackageItems ?? []).map((item) => applySoftCropDefaultsToItem(item, imagesById)),
        );
        storeItem.cartPackage.cartPackageItems = cartPackageItems;
      }
    },

    /**
     * Step 4 (RSPO hosted, issue #14): apply ONE global crop result to every node fed by
     * `subjectImageId`, across every package. One rectangle-crop of a source image trickles to all its
     * nodes (MLivePreview re-projects per slot). Nodes for other images are untouched.
     */
    applyGlobalCrop(subjectImageId: number, result: MCropperResult) {
      for (const storeItem of this.items) {
        if (!storeItem.cartPackage) continue;
        storeItem.cartPackage.cartPackageItems = (storeItem.cartPackage.cartPackageItems ?? []).map((item) =>
          applyGlobalCropResultToItem(item, subjectImageId, result),
        );
      }
    },

    clear() {
      this.items = [];
    },

    /** Gallery hub: set the photo this pass is anchored to (pre-fills its own slot type). */
    setAnchorImage(image?: SubjectImageDisplayModel) {
      this.anchorImage = image;
    },

    /** Record an anchor as ordered (survives `clear()`) so the hub can show its ✓ badge. */
    markAnchorOrdered(imageId: number) {
      if (!this.orderedAnchorImageIds.includes(imageId)) {
        this.orderedAnchorImageIds.push(imageId);
      }
    },

    /** Start a fresh gallery visit: drop the anchor and the ordered-badge history. */
    resetOrderedAnchors() {
      this.orderedAnchorImageIds = [];
      this.anchorImage = undefined;
    },

    updateDataFields(storeItemId: string, fields: SubjectFieldDisplayModel[]) {
      const item = this.items.find((i) => i.id === storeItemId);
      if (item) {
        item.dataFields = fields;
      }
    },

    updateCartPackageItem(storeItemId: string, itemId: identifier, item: Cartv2PackageItemDisplayModel) {
      const storeItem = this.items.find((i) => i.id === storeItemId);
      if (storeItem) {
        storeItem.cartPackage?.cartPackageItems.splice(
          storeItem.cartPackage.cartPackageItems.findIndex((i) => i.id === itemId),
          1,
          item,
        );
      }
    },

    async postCartPackages(jobId: identifier): Promise<ServiceResult<Cartv2PackageDisplayModel>[]> {
      const cartStore = useCartStore();
      await fetchCart(jobId, cartStore.persistedOrderMethod!);
      const results = await Promise.all(
        this.items.map(async (i) => await this.postCartPackage(jobId, i.cartPackage.id)),
      );

      // Hub loop: the pass's anchor photo is now ordered — record it (survives clear()) so the hub
      // shows its ✓ badge. No-op outside the hosted anchor flow (anchorImage undefined).
      if (this.anchorImage) this.markAnchorOrdered(this.anchorImage.id);

      this.clear();
      await cartStore.loadDetails(jobId);

      return results;
    },

    async postCartPackage(
      jobId: identifier,
      cartPackageId: identifier,
    ): Promise<ServiceResult<Cartv2PackageDisplayModel>> {
      const item = this.items?.find((i) => i.cartPackage?.id === cartPackageId);
      if (!item || !item.cartPackage || !item.subject) return { errors: ['An error occurred'] };

      this.saving = true;

      const cartStore = useCartStore();
      if (!cartStore.persistedOrderMethod) {
        console.warn('Order method not set');
      }

      const result = await createCartPackage(
        jobId,
        item.cartPackage.packageId,
        item.subject.id,
        cartStore.persistedOrderMethod!,
        item.cartPackage,
        item.dataFields,
      );
      this.saving = false;
      return result;
    },

    async putCartPackage(jobId: identifier, cartPackageId: identifier): Promise<ServiceResult<boolean>> {
      const item = this.items?.find((i) => i.cartPackage?.id === cartPackageId);

      if (item && item.cartPackage) {
        try {
          this.saving = true;
          await patchCartPackageDetailsToServer(jobId, item.cartPackage);
        } catch (e: any) {
          return { data: false, errors: [e.message] };
        } finally {
          this.saving = false;
        }
      }
      return { data: true };
    },
  },
});
